/**
 * A content language code. The language registry (`GET /languages`, held in
 * `redux/language`) decides which codes exist, so this is a plain string.
 */
export type AssessmentLanguageCode = string;

/**
 * Labels for the launch languages. The language registry supplies the live
 * list; these are what a picker shows until the registry has answered, and
 * what it keeps showing if the request fails.
 */
export const ASSESSMENT_LANGUAGE_LABELS: Record<string, string> = {
    en: 'English',
    hi: 'हिन्दी',
    kn: 'ಕನ್ನಡ',
    mr: 'मराठी',
    bn: 'বাংলা',
};

/** A label the map holds itself, so a code such as "constructor" never reads an inherited member. */
const ownLabel = (
    labels: Record<string, string>,
    code: string
): string | undefined => {
    if (!Object.prototype.hasOwnProperty.call(labels, code)) return undefined;
    const label = labels[code];
    return typeof label === "string" && label ? label : undefined;
};

/**
 * Display name for a language code: the given labels first, then the static
 * launch labels, then the code itself for a language neither knows. Only a
 * label a map holds itself counts.
 */
export function labelForLanguage(
    code: string,
    labels: Record<string, string> = ASSESSMENT_LANGUAGE_LABELS
): string {
    return (
        ownLabel(labels ?? {}, code) ??
        ownLabel(ASSESSMENT_LANGUAGE_LABELS, code) ??
        code
    );
}

export function getQuestionBodyText(question: any, language: string): string {
    if (language && language !== 'en') {
        const translated = question?.questionTranslations?.[language];
        if (translated?.trim()) return translated;
    }
    return (
        question?.questions?.[0] ||
        question?.resourceTitle ||
        'Question text'
    );
}

/** First option label for compact table preview */
export function getFirstOptionPreview(
    question: any,
    language: string,
    maxLen = 48
): string | null {
    const opt = question?.options?.[0];
    if (!opt) return null;
    const text = getOptionDisplayText(opt, language);
    if (!text) return null;
    return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

export function hasTranslation(
    question: any,
    language: string
): boolean {
    if (!language || language === "en") return true;
    const body = question?.questionTranslations?.[language];
    if (body?.trim()) return true;
    return (question?.options || []).some(
        (o: any) => o?.textTranslations?.[language]?.trim()
    );
}

export function getOptionDisplayText(option: any, language: string): string {
    if (language && language !== 'en') {
        const translated = option?.textTranslations?.[language];
        if (translated?.trim()) return translated;
    }
    return option?.text ?? '';
}

/**
 * The languages an organisation or a piece of content offers: 'en' always
 * first, every other code once, in the order given, and only codes in
 * `allowed`. Pass the registry's codes as `allowed` once it has loaded; the
 * default is the static launch list.
 */
export function normalizeAssessmentLanguages(
    langs: string[] | undefined,
    allowed: string[] = Object.keys(ASSESSMENT_LANGUAGE_LABELS)
): AssessmentLanguageCode[] {
    const known = new Set(allowed);
    const others = new Set<string>();
    for (const code of langs || []) {
        if (code !== 'en' && known.has(code)) others.add(code);
    }
    return ['en', ...others];
}
